import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { TRUST_STATS, TRUST_FEATURES, GOOGLE_REVIEW_URL } from '../../lib/businessInfo';

type TrustSignalsVariant = 'strip' | 'grid' | 'bar' | 'stats' | 'features';

interface TrustSignalsProps {
  variant?: TrustSignalsVariant;
  showReviewCta?: boolean;
  className?: string;
}

/**
 * Unified TrustSignals Component - Single Source of Truth
 * 
 * Replaces:
 * - src/pages/cars-under-10k/components/TrustSignals.tsx
 * - src/pages/used-cars-sw-washington/components/TrustSignals.tsx
 * - src/pages/inventory/components/TrustBar.tsx
 * - src/pages/home/components/SocialProofStrip.tsx (stats portion)
 * 
 * @param variant - Layout style: 'strip' | 'grid' | 'bar' | 'stats' | 'features'
 * @param showReviewCta - Show Google review CTA (only for 'stats' variant)
 * @param className - Additional CSS classes
 */
export default function TrustSignals({ 
  variant = 'grid', 
  showReviewCta = false,
  className = '' 
}: TrustSignalsProps) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  const [isBarVisible, setIsBarVisible] = useState(false);

  // Sticky bar scroll trigger
  useEffect(() => {
    if (variant !== 'bar') return;

    const handleScroll = () => {
      setIsBarVisible(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [variant]);

  // ── Variant: Sticky Bar (Inventory Page) ──
  if (variant === 'bar') {
    const barPoints = [
      { icon: TRUST_STATS.docFee.icon, text: TRUST_STATS.docFee.value + ' ' + TRUST_STATS.docFee.label },
      { icon: TRUST_STATS.hiddenFees.icon, text: 'No Dealer Games' },
      { icon: 'ri-eye-line', text: 'Inspected & Priced Honestly' },
      { icon: TRUST_STATS.charity.icon, text: 'Supports Oregon Dog Rescue 🐾' },
    ];

    return (
      <div
        className={`fixed top-0 left-0 right-0 bg-[#1A2332] border-b border-[#E8B84B]/20 shadow-lg transition-all duration-300 z-50 ${
          isBarVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
        style={{ marginTop: '64px' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-3 sm:gap-6 py-2.5 overflow-x-auto scrollbar-hide">
            {barPoints.map((point, index) => (
              <div
                key={index}
                className="flex items-center gap-2 flex-shrink-0 px-3 py-1"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${point.icon} text-[#E8B84B] text-base`}></i>
                </div>
                <span className="text-white text-xs sm:text-sm font-medium whitespace-nowrap">
                  {point.text}
                </span>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
      </div>
    );
  }

  // ── Variant: Stats Grid (Homepage) ──
  if (variant === 'stats') {
    return (
      <section
        ref={ref}
        aria-label="Our commitments and promises"
        className={`bg-[#F7F5F0] border-y border-[#E8E2D6] py-12 px-4 ${className}`}
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
        }}
      >
        <div className="max-w-7xl mx-auto">
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <div className="text-center">
              <dt className="text-sm font-normal text-[#4A5568]">{TRUST_STATS.docFee.label}</dt>
              <dd className="text-3xl sm:text-4xl font-bold text-[#1C2B1E] mt-1">{TRUST_STATS.docFee.value}</dd>
              <dd className="text-xs text-[#4A5568] mt-1">{TRUST_STATS.docFee.description}</dd>
            </div>
            <div className="text-center">
              <dt className="text-sm font-normal text-[#4A5568]">{TRUST_STATS.delivery.label}</dt>
              <dd className="text-3xl sm:text-4xl font-bold text-[#1C2B1E] mt-1">{TRUST_STATS.delivery.value}</dd>
              <dd className="text-xs text-[#4A5568] mt-1">{TRUST_STATS.delivery.description}</dd>
            </div>
            <div className="text-center">
              <dt className="text-sm font-normal text-[#4A5568]">{TRUST_STATS.charity.label}</dt>
              <dd className="text-3xl sm:text-4xl font-bold text-[#1C2B1E] mt-1">{TRUST_STATS.charity.value}</dd>
              <dd className="text-xs text-[#4A5568] mt-1">{TRUST_STATS.charity.description}</dd>
            </div>
            <div className="text-center">
              <dt className="text-sm font-normal text-[#4A5568]">{TRUST_STATS.hiddenFees.label}</dt>
              <dd className="text-3xl sm:text-4xl font-bold text-[#1C2B1E] mt-1">{TRUST_STATS.hiddenFees.value}</dd>
              <dd className="text-xs text-[#4A5568] mt-1">{TRUST_STATS.hiddenFees.description}</dd>
            </div>
          </dl>

          {showReviewCta && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Be Our First Reviewer */}
              <div className="lg:col-span-3 bg-white rounded-xl border border-[#E8E2D6] p-6 shadow-sm">
                <p className="text-[#1A2332] font-bold text-base mb-2">
                  We&apos;re just getting started — and we&apos;re proud of it.
                </p>
                <p className="text-[#4A5568] text-sm leading-relaxed mb-4">
                  No fake reviews. No inflated star counts. Just honest service and a commitment to earning your trust one deal at a time. If you&apos;ve visited us, we&apos;d love to hear from you.
                </p>
                <div className="flex flex-col xs:flex-row flex-wrap gap-3">
                  <a
                    href={GOOGLE_REVIEW_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Leave a Google review for Rockhound Auto Sales (opens in new tab)"
                    className="inline-flex items-center gap-2 bg-[#1A2332] hover:bg-[#2C3E55] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer shadow-sm justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:ring-offset-2"
                  >
                    <i className="ri-google-fill text-[#E8B84B]" aria-hidden="true"></i>
                    Leave a Google Review
                  </a>
                  <Link
                    to="/write-a-review"
                    className="inline-flex items-center gap-2 bg-[#F7F5F0] hover:bg-[#EDE8DE] text-[#1A2332] px-5 py-2.5 rounded-full text-sm font-semibold border border-[#E8E2D6] transition-all whitespace-nowrap cursor-pointer justify-center"
                  >
                    <i className="ri-edit-line" aria-hidden="true"></i>
                    Write a Review
                  </Link>
                </div>
              </div>

              {/* Google CTA */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-[#E8E2D6] p-6 shadow-sm flex flex-col items-center justify-center text-center">
                <div className="w-12 h-12 flex items-center justify-center mb-3" aria-hidden="true">
                  <svg viewBox="0 0 48 48" className="w-10 h-10" aria-hidden="true">
                    <path d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" fill="#FFC107"/>
                    <path d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" fill="#FF3D00"/>
                    <path d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" fill="#4CAF50"/>
                    <path d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" fill="#1976D2"/>
                  </svg>
                </div>
                <p className="text-[#1A2332] font-bold text-base mb-1">Had a great experience?</p>
                <p className="text-[#4A5568] text-xs mb-4 leading-relaxed">Your review helps other Oregon Coast families find an honest dealer.</p>
                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Leave a Google review for Rockhound Auto Sales (opens in new tab)"
                  className="inline-flex items-center gap-2 bg-[#1A2332] hover:bg-[#2C3E55] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer shadow-sm w-full justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:ring-offset-2"
                >
                  <i className="ri-google-fill text-[#E8B84B]" aria-hidden="true"></i>
                  Leave a Google Review
                </a>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  // ── Variant: Strip (SW Washington style) ──
  if (variant === 'strip') {
    const stripStats = [
      TRUST_STATS.grandOpening,
      TRUST_STATS.salesTax,
      TRUST_STATS.charity,
      TRUST_STATS.docFee,
    ];

    return (
      <section className={`py-12 bg-[#F7F5F0] ${className}`}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {stripStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className={`${stat.icon} text-4xl text-[#92650a]`}></i>
                </div>
                <div className="font-oswald text-3xl font-bold text-[#1A2332] mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-[#4A5568]">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── Variant: Features Grid (Cars Under 10k style) ──
  if (variant === 'features') {
    return (
      <section className={`bg-white py-12 border-y border-gray-200 ${className}`}>
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {TRUST_FEATURES.slice(0, 4).map((feature, index) => (
              <div key={index} className="flex flex-col items-center text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-[#E8B84B]/10 rounded-full mb-4">
                  <i className={`${feature.icon} text-3xl text-[#92650a]`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#1C2B1E] mb-2">{feature.title}</h3>
                <p className="text-gray-700">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── Default: Grid (most common) ──
  return (
    <section className={`bg-white py-12 border-y border-gray-200 ${className}`}>
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {TRUST_FEATURES.slice(0, 4).map((feature, index) => (
            <div key={index} className="flex flex-col items-center text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#E8B84B]/10 rounded-full mb-4">
                <i className={`${feature.icon} text-3xl text-[#92650a]`}></i>
              </div>
              <h3 className="text-lg font-bold text-[#1C2B1E] mb-2">{feature.title}</h3>
              <p className="text-gray-700">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}